// ============================================================================
// AVATAR CATALOG PAGE
// Browse and manage avatar definitions - table view with Tailwind styling
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Download, Edit2, Trash2, Copy, Send, Grid, Database, ChevronUp, ChevronDown, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  AvatarDefinition,
  AvatarScope,
  AvatarCategory,
  PublishingStatus
} from '../types';
import {
  browseAvatars,
  deleteAvatar,
  cloneAvatar,
  submitForReview,
  downloadAvatarSOP
} from '../services/avatarCatalogService';
import { EmptyState } from '../components/EmptyState';

type SortField = 'name' | 'category' | 'scope' | 'status' | 'version';
type SortDirection = 'asc' | 'desc';

export default function AvatarCatalog() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [avatars, setAvatars] = useState<AvatarDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedScope, setSelectedScope] = useState<AvatarScope | ''>('');
  const [selectedCategory, setSelectedCategory] = useState<AvatarCategory | ''>('');
  const [showPublicOnly, setShowPublicOnly] = useState(false);
  const [showLatestOnly, setShowLatestOnly] = useState(true);

  const fetchAvatars = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let allAvatars: AvatarDefinition[] = [];

      if (!selectedScope && !selectedCategory && !searchTerm && !showPublicOnly && user?.userId) {
        const [userAvatars, orgAvatars, sessionAvatars] = await Promise.all([
          browseAvatars({ scope: AvatarScope.USER, ownerId: user.userId }).catch(() => ({ avatars: [] })),
          browseAvatars({ scope: AvatarScope.ORGANIZATION, ownerId: user.userId }).catch(() => ({ avatars: [] })),
          browseAvatars({ scope: AvatarScope.SESSION }).catch(() => ({ avatars: [] }))
        ]);

        const combined = [...userAvatars.avatars, ...orgAvatars.avatars, ...sessionAvatars.avatars];
        const uniqueMap = new Map<string, AvatarDefinition>();
        combined.forEach(avatar => {
          const key = `${avatar.avatarDefinitionId}-${avatar.versionNumber}`;
          if (!uniqueMap.has(key)) uniqueMap.set(key, avatar);
        });
        allAvatars = Array.from(uniqueMap.values());
      } else {
        const params: any = {
          scope: selectedScope || undefined,
          category: selectedCategory || undefined,
          isPublic: showPublicOnly || undefined,
          search: searchTerm || undefined
        };
        if ((selectedScope === AvatarScope.USER || selectedScope === AvatarScope.ORGANIZATION) && user?.userId) {
          params.ownerId = user.userId;
        }
        const result = await browseAvatars(params);
        allAvatars = result.avatars;
      }

      if (showLatestOnly) {
        const latestVersionsMap = new Map<string, AvatarDefinition>();
        allAvatars.forEach(avatar => {
          const existing = latestVersionsMap.get(avatar.avatarDefinitionId);
          if (!existing || avatar.versionNumber > existing.versionNumber) {
            latestVersionsMap.set(avatar.avatarDefinitionId, avatar);
          }
        });
        allAvatars = Array.from(latestVersionsMap.values());
      }

      setAvatars(allAvatars);
    } catch (err: any) {
      setError(err.message || 'Failed to load avatars');
    } finally {
      setLoading(false);
    }
  }, [selectedScope, selectedCategory, searchTerm, showPublicOnly, showLatestOnly, user]);

  useEffect(() => { fetchAvatars(); }, [fetchAvatars]);

  const handleSearch = () => fetchAvatars();

  const handleDelete = async (avatarDefinitionId: string, versionNumber: number) => {
    if (!window.confirm('Are you sure you want to delete this avatar? This cannot be undone.')) return;
    try {
      await deleteAvatar(avatarDefinitionId, versionNumber);
      fetchAvatars();
    } catch (err: any) {
      alert(`Failed to delete avatar: ${err.message}`);
    }
  };

  const handleClone = async (avatar: AvatarDefinition) => {
    const newName = window.prompt(`Clone "${avatar.name}" as:`, `${avatar.name} (Copy)`);
    if (!newName) return;
    try {
      const result = await cloneAvatar(avatar.avatarDefinitionId, avatar.versionNumber, newName, user?.userId || '');
      alert(`Avatar cloned successfully! New ID: ${result.avatar.avatarDefinitionId}`);
      fetchAvatars();
    } catch (err: any) {
      alert(`Failed to clone avatar: ${err.message}`);
    }
  };

  const handlePublish = async (avatar: AvatarDefinition) => {
    const notes = window.prompt(`Submit "${avatar.name}" for review. Add notes (optional):`);
    if (notes === null) return;
    try {
      await submitForReview(avatar.avatarDefinitionId, avatar.versionNumber, user?.userId || '', user?.organizationId || '', notes || undefined);
      alert('Avatar submitted for review!');
      fetchAvatars();
    } catch (err: any) {
      alert(`Failed to submit for review: ${err.message}`);
    }
  };

  const handleDownloadSOP = async (avatar: AvatarDefinition) => {
    try {
      await downloadAvatarSOP(avatar.avatarDefinitionId, avatar.name, avatar.versionNumber);
    } catch (err: any) {
      alert(`Failed to download SOP: ${err.message}`);
    }
  };

  const getCategoryClasses = (category: AvatarCategory): string => {
    const map: Record<AvatarCategory, string> = {
      [AvatarCategory.FACILITATOR]: 'bg-indigo-100 text-indigo-700',
      [AvatarCategory.DOMAIN_EXPERT]: 'bg-blue-100 text-blue-700',
      [AvatarCategory.STAKEHOLDER]: 'bg-emerald-100 text-emerald-700',
      [AvatarCategory.RESEARCHER]: 'bg-cyan-100 text-cyan-700',
      [AvatarCategory.CUSTOM]: 'bg-violet-100 text-violet-700'
    };
    return map[category] || 'bg-slate-100 text-slate-700';
  };

  const getStatusClasses = (status: PublishingStatus): string => {
    const map: Record<PublishingStatus, string> = {
      [PublishingStatus.DRAFT]: 'bg-slate-100 text-slate-600',
      [PublishingStatus.PENDING_REVIEW]: 'bg-amber-100 text-amber-700',
      [PublishingStatus.APPROVED]: 'bg-green-100 text-green-700',
      [PublishingStatus.REJECTED]: 'bg-red-100 text-red-700'
    };
    return map[status];
  };

  const filteredAvatars = avatars.filter(avatar => {
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      return avatar.name.toLowerCase().includes(query) || avatar.description?.toLowerCase().includes(query) || avatar.keywords?.some(k => k.toLowerCase().includes(query));
    }
    return true;
  });

  const sortedAvatars = [...filteredAvatars].sort((a, b) => {
    let aValue: any, bValue: any;
    switch (sortField) {
      case 'name': aValue = a.name.toLowerCase(); bValue = b.name.toLowerCase(); break;
      case 'category': aValue = a.category; bValue = b.category; break;
      case 'scope': aValue = a.scope; bValue = b.scope; break;
      case 'status': aValue = a.publishingStatus; bValue = b.publishingStatus; break;
      case 'version': aValue = a.versionNumber; bValue = b.versionNumber; break;
      default: aValue = a.name; bValue = b.name;
    }
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const SortableHeader: React.FC<{ field: SortField; children: React.ReactNode }> = ({ field, children }) => (
    <th
      onClick={() => handleSort(field)}
      className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400 cursor-pointer select-none hover:text-[#052831] transition-colors"
    >
      <div className="flex items-center gap-1.5">
        {children}
        <SortIcon field={field} />
      </div>
    </th>
  );

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Users size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Avatar Catalog</h1>
              <p className="text-slate-500 text-sm font-medium">Browse, create, and manage AI avatars for your workshops</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/avatars/groups')}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
            >
              <Grid size={16} />
              Avatar Groups
            </button>
            <button
              onClick={() => navigate('/avatars/create')}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
            >
              <Plus size={16} />
              Create Avatar
            </button>
          </div>
        </header>

        {/* Filters + Table — one unit */}
        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#e2e8f0]">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex-1 min-w-[280px] relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="text"
                  placeholder="Search avatars..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-[#e2e8f0] bg-white text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-slate-400 text-sm"
                />
              </div>
              <select
                value={selectedScope}
                onChange={(e) => setSelectedScope(e.target.value as AvatarScope | '')}
                className="pl-3 pr-8 py-2.5 rounded-xl border border-[#e2e8f0] text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none cursor-pointer"
              >
                <option value="">All Scopes</option>
                <option value={AvatarScope.SESSION}>Session</option>
                <option value={AvatarScope.USER}>User</option>
                <option value={AvatarScope.ORGANIZATION}>Organization</option>
              </select>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as AvatarCategory | '')}
                className="pl-3 pr-8 py-2.5 rounded-xl border border-[#e2e8f0] text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none cursor-pointer"
              >
                <option value="">All Categories</option>
                <option value={AvatarCategory.FACILITATOR}>Facilitator</option>
                <option value={AvatarCategory.DOMAIN_EXPERT}>Domain Expert</option>
                <option value={AvatarCategory.STAKEHOLDER}>Stakeholder</option>
                <option value={AvatarCategory.RESEARCHER}>Researcher</option>
                <option value={AvatarCategory.CUSTOM}>Custom</option>
              </select>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-navy">
                <input type="checkbox" checked={showPublicOnly} onChange={(e) => setShowPublicOnly(e.target.checked)} className="rounded border-slate-300 text-primary focus:ring-primary" />
                Public only
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-navy">
                <input type="checkbox" checked={showLatestOnly} onChange={(e) => setShowLatestOnly(e.target.checked)} className="rounded border-slate-300 text-primary focus:ring-primary" />
                Latest only
              </label>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20 text-slate-400">
              <div className="text-sm font-medium">Loading avatars...</div>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-sm text-red-700">Failed to load avatars: {error}</p>
            </div>
          ) : sortedAvatars.length === 0 ? (
            <EmptyState
              icon={Users}
              title={searchTerm || selectedScope || selectedCategory ? 'No avatars found' : 'No avatars yet'}
              description={searchTerm || selectedScope || selectedCategory ? 'Try adjusting your filters' : 'Create your first avatar to get started'}
              action={
                <button
                  onClick={() => searchTerm || selectedScope || selectedCategory
                    ? (() => { setSearchTerm(''); setSelectedScope(''); setSelectedCategory(''); })()
                    : navigate('/avatars/create')
                  }
                  className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
                >
                  {searchTerm || selectedScope || selectedCategory ? 'Clear Filters' : 'Create Avatar'}
                </button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                    <SortableHeader field="name">Name</SortableHeader>
                    <SortableHeader field="category">Category</SortableHeader>
                    <SortableHeader field="scope">Scope</SortableHeader>
                    <SortableHeader field="status">Status</SortableHeader>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Features</th>
                    <SortableHeader field="version">Version</SortableHeader>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {sortedAvatars.map((avatar) => (
                    <tr
                      key={`${avatar.avatarDefinitionId}-${avatar.versionNumber}`}
                      onClick={() => navigate(`/avatars/${avatar.avatarDefinitionId}/edit`)}
                      className="cursor-pointer hover:bg-slate-50/50 transition-colors"
                    >
                      <td className="px-4 py-4 max-w-[250px]">
                        <div className="font-medium text-navy">{avatar.name}</div>
                        <div className="text-xs text-slate-500 truncate mt-0.5">{avatar.description}</div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold ${getCategoryClasses(avatar.category)}`}>
                          {avatar.category}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-xs text-slate-500">{avatar.scope}</td>
                      <td className="px-4 py-4">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold ${getStatusClasses(avatar.publishingStatus)}`}>
                          {avatar.publishingStatus}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-1.5 flex-wrap">
                          {(avatar.kbConfig?.enabled || (avatar as any).updates?.kbConfig?.enabled) && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-primary/10 border border-primary/30 rounded text-[10px] font-bold text-navy" title="Knowledge Base enabled">
                              <Database size={10} /> KB
                            </span>
                          )}
                          {avatar.supportsPhysicalDevice && (
                            <span className="px-1.5 py-0.5 bg-green-100 border border-green-300 rounded text-[10px] font-bold text-green-700" title="Physical Device support">
                              Device
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-xs text-slate-400">v{avatar.versionNumber}</td>
                      <td className="px-4 py-4">
                        <div className="flex gap-1.5 justify-end" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => navigate(`/avatars/${avatar.avatarDefinitionId}/edit`)}
                            className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-all"
                            title="Edit"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleClone(avatar)}
                            className="p-1.5 rounded-lg border border-[#e2e8f0] text-slate-500 hover:text-violet-600 hover:border-violet-300 transition-all"
                            title="Clone"
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            onClick={() => handleDownloadSOP(avatar)}
                            className="p-1.5 rounded-lg border border-[#e2e8f0] text-slate-500 hover:text-blue-600 hover:border-blue-300 transition-all"
                            title="Download SOP"
                          >
                            <Download size={14} />
                          </button>
                          {avatar.publishingStatus === PublishingStatus.DRAFT && (
                            <button
                              onClick={() => handlePublish(avatar)}
                              className="p-1.5 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-all"
                              title="Submit for Review"
                            >
                              <Send size={14} />
                            </button>
                          )}
                          {avatar.publishingStatus === PublishingStatus.DRAFT && (
                            <button
                              onClick={() => handleDelete(avatar.avatarDefinitionId, avatar.versionNumber)}
                              className="p-1.5 rounded-lg border border-red-200 text-red-400 hover:text-red-600 hover:border-red-300 transition-all"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
